import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useAuth } from "../../../auth/store";
import { useCan } from "../../../auth/roles";
import { ErrorState } from "../../../components/States";
import { ConfirmDialog, DataTable, PageHeader, RowMenu, useToast } from "../../../components/ui";
import { backendApi, backendKeys, type RouteStats, type ServiceStats } from "../api";
import { BackendActions, BackendRangePicker, BackendTabs, ConnectPrompt, ms, pct, useBackendRange, when } from "../components";

function ErrorRate({ errors, total }: { errors: number; total: number }) {
  const rate = total ? errors / total : 0;
  return <span className={`mono${rate >= 0.05 ? " be-bad" : rate >= 0.01 ? " be-warn" : ""}`}>{pct(errors, total)}</span>;
}

function useBoost() {
  const auth = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (service: string) => backendApi.boost(auth.currentProjectId!, { service, minutes: 60 }),
    onSuccess: (_d, service) => {
      toast.push("success", `${service} captures everything for the next hour.`);
      void qc.invalidateQueries({ queryKey: backendKeys.settings(auth.currentProjectId) });
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });
}

export function ServicesPage() {
  const auth = useAuth();
  const can = useCan();
  const navigate = useNavigate();
  const [range] = useBackendRange();
  const q = useQuery({ queryKey: backendKeys.services(auth.currentProjectId, range), queryFn: () => backendApi.services(range), enabled: !!auth.currentProjectId });
  const boost = useBoost();
  const [boosting, setBoosting] = useState<string | null>(null);
  const rows = q.data?.items ?? [];

  return (
    <>
      <PageHeader title="Backend" actions={<BackendActions />}>
        <BackendTabs />
      </PageHeader>
      {q.isError ? (
        <div className="card section"><ErrorState error={q.error} retry={() => void q.refetch()} /></div>
      ) : !q.isPending && rows.length === 0 ? (
        <ConnectPrompt title="No services yet" hint="Each service you connect appears here with its request volume, errors, and response times." />
      ) : (
        <div className="card table-scroll stack-table be-table">
          <DataTable<ServiceStats>
            ariaLabel="Services"
            rows={rows}
            rowKey={(s) => s.service}
            loading={q.isPending}
            onRowClick={(s) => void navigate({ to: "/backend/services/$service" as never, params: { service: s.service } as never, search: (range === "24h" ? {} : { range }) as never })}
            columns={[
              { key: "svc", header: "Service", render: (s) => (<span className="cell-stack"><strong>{s.service}</strong><small>Last seen {when(s.lastSeen)}</small></span>) },
              { key: "req", header: "Requests", label: "Requests", align: "right", render: (s) => <span className="mono">{s.requests.toLocaleString()}</span> },
              { key: "err", header: "Errors", label: "Errors", align: "right", render: (s) => <ErrorRate errors={s.errors} total={s.requests} /> },
              { key: "p50", header: "Median", label: "Median", align: "right", render: (s) => <span className="mono">{ms(s.p50)}</span> },
              { key: "p95", header: "95th", label: "95th percentile", align: "right", render: (s) => <span className="mono">{ms(s.p95)}</span> },
              { key: "logs", header: "Error logs", label: "Error logs", align: "right", render: (s) => <span className="mono">{s.errorLogs.toLocaleString()}</span> },
              {
                key: "act",
                header: "",
                align: "right",
                render: (s) => (
                  <span onClick={(e) => e.stopPropagation()}>
                    <RowMenu label={`Actions for ${s.service}`}>
                      <Link role="menuitem" to={"/backend" as never} search={{ service: s.service, ...(range === "24h" ? {} : { range }) } as never}>View requests</Link>
                      <Link role="menuitem" to={"/backend/logs" as never} search={{ service: s.service, ...(range === "24h" ? {} : { range }) } as never}>View logs</Link>
                      {can.write && <button type="button" role="menuitem" onClick={() => setBoosting(s.service)}>Capture everything for 1 hour</button>}
                    </RowMenu>
                  </span>
                ),
              },
            ]}
          />
        </div>
      )}
      <ConfirmDialog
        open={!!boosting}
        title="Capture everything for 1 hour"
        confirmLabel="Capture everything"
        busy={boost.isPending}
        onClose={() => setBoosting(null)}
        onConfirm={() => boosting && boost.mutate(boosting, { onSuccess: () => setBoosting(null) })}
      >
        <p style={{ margin: 0 }}>
          <strong>{boosting}</strong> keeps every request and log line for the next hour, then returns to its usual level. This uses more of your backend data allowance.
        </p>
      </ConfirmDialog>
    </>
  );
}

export function ServiceDetailPage() {
  const { service } = useParams({ strict: false }) as { service: string };
  const auth = useAuth();
  const navigate = useNavigate();
  const [range] = useBackendRange();
  const q = useQuery({ queryKey: backendKeys.routes(auth.currentProjectId, service, range), queryFn: () => backendApi.routes(service, range), enabled: !!auth.currentProjectId });
  const rangeSearch = range === "24h" ? {} : { range };
  return (
    <>
      <PageHeader
        title={service}
        subtitle="Requests by route"
        back={<Link to={"/backend/services" as never} search={rangeSearch as never} className="btn ghost icon-btn" aria-label="Back to services" title="Back to services">←</Link>}
        actions={
          <>
            <Link to={"/backend/logs" as never} search={{ service, ...rangeSearch } as never} className="btn">View logs</Link>
            <BackendRangePicker />
          </>
        }
      />
      {q.isError ? (
        <div className="card section"><ErrorState error={q.error} retry={() => void q.refetch()} /></div>
      ) : (
        <div className="card table-scroll stack-table be-table">
          <DataTable<RouteStats>
            ariaLabel={`Routes in ${service}`}
            rows={q.data?.items ?? []}
            rowKey={(r) => r.route}
            loading={q.isPending}
            onRowClick={(r) => void navigate({ to: "/backend" as never, search: { service, route: r.route, ...rangeSearch } as never })}
            empty="No requests for this service in this time range."
            columns={[
              { key: "route", header: "Route", render: (r) => <strong className="mono">{r.route || "(no route)"}</strong> },
              { key: "req", header: "Requests", label: "Requests", align: "right", render: (r) => <span className="mono">{r.requests.toLocaleString()}</span> },
              { key: "err", header: "Errors", label: "Errors", align: "right", render: (r) => <ErrorRate errors={r.errors} total={r.requests} /> },
              { key: "p50", header: "Median", label: "Median", align: "right", render: (r) => <span className="mono">{ms(r.p50)}</span> },
              { key: "p95", header: "95th", label: "95th percentile", align: "right", render: (r) => <span className="mono">{ms(r.p95)}</span> },
              { key: "p99", header: "99th", label: "99th percentile", align: "right", render: (r) => <span className="mono">{ms(r.p99)}</span> },
            ]}
          />
        </div>
      )}
    </>
  );
}
